import {
  BellOutlined,
  LogoutOutlined,
  SafetyOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { history, useModel } from '@umijs/max';
import type { MenuProps } from 'antd';
import { Spin } from 'antd';
import { createStyles } from 'antd-style';
import React from 'react';
import { flushSync } from 'react-dom';
import { clearAllPageState } from '@/hooks/usePageState';
import { stopTokenRefreshTimer } from '@/utils/request';
import { removeToken } from '@/utils/token';
import HeaderDropdown from '../HeaderDropdown';

export type GlobalHeaderRightProps = {
  menu?: boolean;
  children?: React.ReactNode;
};

export const AvatarName = () => {
  const { initialState } = useModel('@@initialState');
  const { currentUser } = initialState || {};
  return <span className="anticon">{currentUser?.name}</span>;
};

const useStyles = createStyles(({ token }) => {
  return {
    action: {
      display: 'flex',
      height: '48px',
      marginLeft: 'auto',
      overflow: 'hidden',
      alignItems: 'center',
      padding: '0 8px',
      cursor: 'pointer',
      borderRadius: token.borderRadius,
      '&:hover': {
        backgroundColor: token.colorBgTextHover,
      },
    },
  };
});

export const AvatarDropdown: React.FC<GlobalHeaderRightProps> = ({
  menu,
  children,
}) => {
  const { styles } = useStyles();

  const { initialState, setInitialState } = useModel('@@initialState');

  /**
   * 退出登录
   * 1. 清除 Token（从 localStorage 中移除）
   * 2. 重置 initialState（清除 currentUser、permissions、menus、isRoot）
   * 3. 重定向到登录页
   */
  const handleLogout = () => {
    // 1. 清除 Token
    removeToken();
    // 停止 Token 定时刷新
    stopTokenRefreshTimer();
    // 清除页面状态缓存（搜索条件等）
    clearAllPageState();

    // 2. 重置 initialState
    flushSync(() => {
      setInitialState((s) => ({
        ...s,
        currentUser: undefined,
        permissions: undefined,
        menus: undefined,
        isRoot: undefined,
      }));
    });

    // 3. 重定向到登录页
    const { search, pathname } = window.location;
    const urlParams = new URL(window.location.href).searchParams;
    const redirect = urlParams.get('redirect');
    // 如果当前不在登录页且没有 redirect 参数，则跳转到登录页并保存当前路径
    if (pathname !== '/user/login' && !redirect) {
      const searchParams = new URLSearchParams({
        redirect: pathname + search,
      });
      history.replace({
        pathname: '/user/login',
        search: searchParams.toString(),
      });
    }
  };

  const onMenuClick: MenuProps['onClick'] = (event) => {
    const { key } = event;
    if (key === 'logout') {
      handleLogout();
      return;
    }

    // 检测当前平台前缀
    const pathname = window.location.pathname;
    const match = pathname.match(/^\/(cdn-oper|saas-platform)/);
    const platform = match?.[1] || '';

    // 构建目标路径
    let targetPath: string;
    if (platform) {
      if (key === 'notifications') {
        // notifications 在 workbench 下
        targetPath = `/${platform}/workbench/notifications`;
      } else {
        targetPath = `/${platform}/account/${key}`;
      }
    } else {
      targetPath = `/account/${key}`;
    }

    history.push(targetPath);
  };

  const loading = (
    <span className={styles.action}>
      <Spin
        size="small"
        style={{
          marginLeft: 8,
          marginRight: 8,
        }}
      />
    </span>
  );

  if (!initialState) {
    return loading;
  }

  const { currentUser } = initialState;

  if (!currentUser?.name) {
    return loading;
  }

  const menuItems = [
    ...(menu
      ? [
          {
            key: 'profile',
            icon: <UserOutlined />,
            label: '个人资料',
          },
          {
            key: 'security',
            icon: <SafetyOutlined />,
            label: '安全设置',
          },
          {
            key: 'notifications',
            icon: <BellOutlined />,
            label: '消息通知',
          },
          {
            type: 'divider' as const,
          },
        ]
      : []),
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
    },
  ];

  return (
    <HeaderDropdown
      menu={{
        selectedKeys: [],
        onClick: onMenuClick,
        items: menuItems,
      }}
    >
      {children}
    </HeaderDropdown>
  );
};
